/**
 * アプリのベースパス (React Router の basename) を考慮したナビゲーション補助。
 *
 * SAITE などパートナーによっては `VITE_APP_BASENAME=/app` のように
 * サブパス配信されるため、`window.location.href = '/'` のような絶対パス指定は
 * basename を取りこぼし `/app/` プレフィックスが落ちてしまう。
 * 内部パスへハードナビゲートする際は必ずこのヘルパーを経由すること。
 */

import { brandConfig } from '../config/brands';

// 正規化: 末尾スラッシュを除去し、'/' (ルート) は空文字に畳む。
const BASENAME: string = (() => {
  const raw = import.meta.env.VITE_APP_BASENAME ?? '/';
  if (raw === '/' || raw === '') return '';
  return raw.replace(/\/$/, '');
})();

type SpaNavigate = (path: string) => void;
let spaNavigate: SpaNavigate | null = null;

/** RouterProvider 外の UI からも React Router の SPA 遷移を利用できるよう登録する。 */
export function registerSpaNavigate(navigate: SpaNavigate): void {
  spaNavigate = navigate;
}

/** 登録済み Router があれば SPA 遷移し、起動前だけハードナビゲートへフォールバックする。 */
export function navigateInApp(path: string): void {
  if (spaNavigate) {
    spaNavigate(path);
    return;
  }
  hardNavigate(path);
}

/**
 * 引数がアプリ内パスとして安全か (= 同一オリジンに閉じるか) を判定する。
 *
 * 拒否するもの:
 * - スキーム付き (`https://evil.example`, `javascript:...` 等) — 別オリジンへ出る
 * - `//evil.example` / `/\evil.example` — ブラウザにプロトコル相対 URL として解釈され、
 *   basename 未設定 (BASENAME='') では location への代入がそのまま外部遷移になる
 *
 * window.location.pathname は正規化されない (`https://host//evil.example` の pathname は
 * `//evil.example`) ため、URL 由来の値は必ずこれを通すこと。
 */
export function isSafeAppPath(path: string): boolean {
  if (/^[a-z][a-z0-9+.-]*:/i.test(path)) return false;
  const p = path.startsWith('/') ? path : `/${path}`;
  if (p === '/') return true;
  const pathOnly = p.split(/[?#]/)[0];
  return /^\/[^/\\]/.test(p) && !pathOnly.includes('\\');
}

/**
 * アプリ内パスに basename を前置した絶対パスを返す。**同一オリジン専用**。
 *
 * 完全な URL やプロトコル相対 URL は受け付けず、ホームに落とす。バックエンドの
 * redirect_to など外部由来の値がそのまま location へ渡る経路があるため、呼び出し側の
 * 検証に頼らずここで止める。外部サイトへのリンクは appPath() を通さず直接 href に置くこと
 * (例: termsHref() はパートナー設定の外部 URL をそのまま返す)。
 */
export function appPath(path: string = '/'): string {
  if (!isSafeAppPath(path)) {
    console.warn('[navigation] unsafe app path rejected:', path);
    return `${BASENAME}/`;
  }
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${BASENAME}${p}`;
}

/**
 * アプリ内パスの完全 URL (origin + basename + パス) を返す。
 * Stripe の return_url や共有リンクなど、外部サービス・クリップボードへ渡す
 * 完全 URL はここで組み立てる。`${window.location.origin}/...` の直書きは
 * basename (/app) が落ちるため ESLint (eslint.url-guard.config.js) で禁止している。
 */
export function absoluteAppUrl(path: string = '/'): string {
  return `${window.location.origin}${appPath(path)}`;
}

/**
 * 現在の URL から basename を取り除いたアプリ内パスを返す (?query, #hash 込み)。
 * appPath() の逆変換にあたり、戻り値はそのまま appPath() / hardNavigate() に渡せる。
 *
 * basename 配下でない URL (例: VITE_APP_BASENAME=/app で saite.co.jp/ を開いた場合) は
 * アプリ内パスとして復元できないため '/' を返す。
 */
export function currentAppPath(): string {
  if (typeof window === 'undefined') return '/';
  const { pathname, search, hash } = window.location;
  return stripBasename(pathname, BASENAME) + search + hash;
}

/** currentAppPath() の純粋部分 (テスト用に basename を引数で受ける)。 */
export function stripBasename(pathname: string, basename: string = BASENAME): string {
  if (!basename) return pathname || '/';
  if (pathname === basename) return '/';
  if (pathname.startsWith(`${basename}/`)) return pathname.slice(basename.length) || '/';
  return '/';
}

/**
 * 利用規約リンクの href を返す。
 * brandConfig.urls.terms が設定されていれば外部URLをそのまま、
 * 未設定ならアプリ内ページ (/lp/terms) の basename 付きパスを返す。
 *
 * 外部 URL を許すのはここだけ。値の出所はビルド時のブランド設定 (実行時入力ではない) で、
 * 同一オリジン限定の appPath() を通すと外部規約ページへのリンクが壊れるため分けている。
 */
export function termsHref(): string {
  return brandConfig.urls.terms ?? appPath('/lp/terms');
}

/** 利用規約リンクが外部URLか (外部なら target="_blank" を付与する判定に使う)。 */
export function isExternalTerms(): boolean {
  return !!brandConfig.urls.terms;
}

/**
 * basename を考慮したハードナビゲート (完全リロードを伴う遷移)。
 * react-query キャッシュやローカル状態を確実にリセットしたい場面で使う。
 */
export function hardNavigate(path: string = '/'): void {
  if (typeof window !== 'undefined') {
    window.location.href = appPath(path);
  }
}
